import { Link, useLocation } from "react-router-dom";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import favicon from "./assets/favicon.png";
import { Brand } from "./Brand.js";
import { SidebarIcon } from "./SidebarIcon.js";
import { readChoice, writeChoice } from "./preferences.js";
import { QuickActionsMenu } from "./QuickActionsMenu.js";
import type { StaffQuickAction } from "./quickActions.js";
import { lockDocumentScroll } from "./ui/documentScrollLock.js";

export type SidebarItem = {
  to: string;
  label: string;
  group?: string;
  badge?: ReactNode;
  /** Additional routes that belong to this destination without sharing its path prefix. */
  activeFor?: readonly string[];
};

type SidebarBrand = {
  title: string;
  subtitle: string;
};

const defaultBrand: SidebarBrand = {
  title: "Wirral Music Factory",
  subtitle: "Staff Hub",
};

function hubSubtitle(subtitle: string): string {
  return subtitle.replace(/\bhub$/, "Hub");
}

function matchesRoute(pathname: string, route: string): boolean {
  return (
    pathname === route || (route !== "/" && pathname.startsWith(`${route}/`))
  );
}

/**
 * A sidebar destination stays selected on its child routes, plus any legacy or
 * sibling route explicitly assigned to the same workspace.
 */
export function sidebarItemIsActive(
  item: SidebarItem,
  pathname: string,
): boolean {
  return (
    matchesRoute(pathname, item.to) ||
    item.activeFor?.some((route) => matchesRoute(pathname, route)) === true
  );
}

/**
 * Describe why an active sidebar item is current without calling a parent or
 * sibling workspace link the current page. Assistive technology can therefore
 * distinguish the exact destination from the broader location containing it.
 */
export function sidebarItemAriaCurrent(
  item: SidebarItem,
  pathname: string,
): "page" | "location" | undefined {
  if (pathname === item.to) return "page";
  return sidebarItemIsActive(item, pathname) ? "location" : undefined;
}

/** What a browser is allowed to remember about a grouped heading. */
const GROUP_STATES = ["collapsed", "expanded"] as const;

/**
 * Whether a group starts shut is a settled preference, so it outlives the tab.
 *
 * The heading is normalised into a key that is safe to store and to read back.
 * It is derived from the label, so renaming a heading starts it fresh rather
 * than inheriting what the old name remembered — the safe way round, since a
 * forgotten preference costs one click and an inherited one confuses. Two
 * headings differing only in punctuation would share a key; the staff
 * navigation has no such pair.
 */
function groupPreferenceKey(label: string): string {
  return `nav.group.${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function groupsCollapsedByPreference(
  items: readonly SidebarItem[],
): Set<string> {
  const collapsed = new Set<string>();
  for (const item of items) {
    if (item.group === undefined || collapsed.has(item.group)) continue;
    if (
      readChoice(groupPreferenceKey(item.group), GROUP_STATES) === "collapsed"
    ) {
      collapsed.add(item.group);
    }
  }
  return collapsed;
}

/**
 * Whether a badge will actually draw something.
 *
 * `badge` is a ReactNode, which legally holds null, false and true — and
 * `badge={count > 0 && <Tag />}` is exactly the idiom that yields them. React
 * draws nothing for any of those, so testing only for `undefined` would put an
 * empty box on a folded heading claiming there is something to see.
 */
function badgeShows(badge: ReactNode): boolean {
  return (
    badge !== undefined &&
    badge !== null &&
    typeof badge !== "boolean" &&
    badge !== ""
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Matching the focusable selector is not the same as being reachable.
 *
 * A collapsed group's links and the account menu's buttons are both in the
 * drawer's markup while being unreachable by keyboard, and a focus trap that
 * counts them sends Tab to an element that cannot take focus — which does not
 * move focus at all, so the trap it was meant to close leaks instead.
 */
function isReachable(element: HTMLElement): boolean {
  if (element.closest("[hidden]") !== null) return false;
  for (
    let parent = element.parentElement;
    parent !== null;
    parent = parent.parentElement
  ) {
    // A summary is the control that opens its own details, so a shut details
    // hides everything inside it except that.
    const ownSummary =
      element.tagName === "SUMMARY" && element.parentElement === parent;
    if (parent instanceof HTMLDetailsElement && !parent.open && !ownSummary)
      return false;
  }
  return true;
}

/** A focus target should have entered the viewport, not just become mounted. */
function isVisiblyOnScreen(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.visibility === "collapse" ||
    (style.opacity.trim() !== "" && Number(style.opacity) === 0)
  ) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  // JSDOM has no layout boxes. Its visibility comes from the DOM/style state;
  // real browsers have a rectangle that lets us wait for the drawer motion.
  if (rect.width === 0 && rect.height === 0 && element.getClientRects().length === 0) {
    return true;
  }
  return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 &&
    rect.left < window.innerWidth && rect.top < window.innerHeight;
}

export function Sidebar({
  items,
  footer,
  quickActions = [],
  brand = defaultBrand,
  headerAction,
  mobileQuickItems,
}: {
  items: SidebarItem[];
  footer: ReactNode;
  quickActions?: StaffQuickAction[];
  brand?: SidebarBrand;
  /**
   * Something that belongs in the persistent chrome rather than on a page —
   * the notification bell, today.
   *
   * Placed in whichever piece of chrome the viewport is actually using: the
   * sidebar head above 900px, where the sidebar is a permanent column, and the
   * mobile bar below it, where the sidebar is a drawer that is shut most of the
   * time. **One of them, not both hidden by CSS.** The drawer traps focus while
   * it is open and the trap reads the DOM rather than the stylesheet, so a
   * `display: none` copy inside it would be a control Tab lands on and nobody
   * can see.
   */
  headerAction?: ReactNode;
  /** Optional family Home shortcuts; More opens the same accessible drawer. */
  mobileQuickItems?: SidebarItem[];
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  /**
   * False until measured, which puts `headerAction` in the sidebar head.
   *
   * That is the right default for a server render and for anything without
   * `matchMedia`: the sidebar head is always in the document, while the mobile
   * bar is only drawn below 900px.
   */
  const [compact, setCompact] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(
    () => groupsCollapsedByPreference(items),
  );
  const navigationId = useId();
  const drawerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  // The family shortcut bar is not rendered while the drawer is open, so when
  // More opened it, focus can only go back once the bar has mounted again.
  const openedFromMore = useRef(false);
  const restoreFocusToMore = useRef(false);
  const focusRequest = useRef(0);
  const quickActionDestination = useRef<string | null>(null);
  const brandSubtitle = hubSubtitle(brand.subtitle);

  useEffect(() => {
    setOpen(false);
    if (quickActionDestination.current === pathname) {
      quickActionDestination.current = null;
      document.getElementById("main-content")?.focus();
    }
  }, [pathname]);

  // The same query the stylesheet collapses the shell at, and the same one the
  // drawer already watched to close itself. Tracked rather than only listened
  // to, because `headerAction` has to be rendered in one place or the other and
  // the answer changes when the window does.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const mobile = window.matchMedia("(max-width: 900px)");
    const follow = () => {
      setCompact(mobile.matches);
      if (!mobile.matches) setOpen(false);
    };
    follow();
    mobile.addEventListener("change", follow);
    return () => mobile.removeEventListener("change", follow);
  }, []);

  useLayoutEffect(() => {
    if (!open || !compact || !drawerRef.current) return;

    const releaseScrollLock = lockDocumentScroll(drawerRef.current);
    const main = document.getElementById("main-content");
    const mainWasInert = main?.hasAttribute("inert") ?? false;
    const previousAriaHidden = main?.getAttribute("aria-hidden") ?? null;
    main?.setAttribute("inert", "");
    main?.setAttribute("aria-hidden", "true");
    return () => {
      releaseScrollLock();
      if (!mainWasInert) main?.removeAttribute("inert");
      if (previousAriaHidden === null) main?.removeAttribute("aria-hidden");
      else main?.setAttribute("aria-hidden", previousAriaHidden);
    };
  }, [open, compact]);

  // Opening a drawer starts a CSS transform. A zero-delay timer can focus the
  // close button while it is still entirely off-screen, and keyboard
  // activation may then leave focus on the header toggle. Follow rendered
  // visibility frame by frame until a reachable control is on-screen. The
  // request token makes a cancelled transition, close/reopen race or unmount
  // unable to run an older focus request against the current drawer state.
  useLayoutEffect(() => {
    const request = ++focusRequest.current;
    if (!open || !compact) return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    let frame = 0;

    const focusVisibleControl = () => {
      if (
        focusRequest.current !== request ||
        !drawer.isConnected ||
        drawer.dataset.open !== "true"
      ) return;

      const visible = window.getComputedStyle(drawer);
      if (
        visible.display === "none" ||
        visible.visibility === "hidden" ||
        visible.visibility === "collapse"
      ) {
        frame = window.requestAnimationFrame(focusVisibleControl);
        return;
      }

      const target = Array.from(
        drawer.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).find((element) => isReachable(element) && isVisiblyOnScreen(element));
      if (!target) {
        frame = window.requestAnimationFrame(focusVisibleControl);
        return;
      }

      target.focus({ preventScroll: true });
      // Some browsers can refuse focus while a visibility transition is
      // settling. Confirm the active element instead of assuming focus() won.
      if (document.activeElement !== target) {
        frame = window.requestAnimationFrame(focusVisibleControl);
      }
    };

    frame = window.requestAnimationFrame(focusVisibleControl);
    return () => {
      if (focusRequest.current === request) focusRequest.current += 1;
      window.cancelAnimationFrame(frame);
    };
  }, [open, compact]);

  // Focus briefly remains on the header toggle after keyboard activation,
  // before the drawer's first visible control can take it. Capture Escape at
  // document level so closing is available during that hand-off as well.
  useEffect(() => {
    if (!open || !compact) return;
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closeAndRestoreFocus();
    };
    document.addEventListener("keydown", closeOnEscape, true);
    return () => document.removeEventListener("keydown", closeOnEscape, true);
  }, [open, compact]);

  const sections = items.reduce<
    Array<{ label?: string; items: SidebarItem[] }>
  >((groups, item) => {
    const previous = groups.at(-1);
    if (previous && previous.label === item.group) {
      previous.items.push(item);
    } else {
      groups.push({ label: item.group, items: [item] });
    }
    return groups;
  }, []);
  const activeGroup = items.find(
    (item) => item.group !== undefined && sidebarItemIsActive(item, pathname),
  )?.group;

  // When navigation arrives at a page inside a collapsed group, reveal the
  // current location again — on the first render too, so somebody who reloads
  // or deep-links into a group they keep shut can still see where they are.
  // The reveal is for this visit only and does not rewrite what is remembered,
  // so the group is shut again next time unless they say so themselves. They
  // can also re-collapse it while staying on the page; it reopens only after a
  // subsequent navigation.
  useEffect(() => {
    if (!activeGroup) return;
    setCollapsedGroups((current) => {
      if (!current.has(activeGroup)) return current;
      const next = new Set(current);
      next.delete(activeGroup);
      return next;
    });
  }, [pathname, activeGroup]);

  function closeAndRestoreFocus() {
    setOpen(false);
    if (openedFromMore.current) {
      restoreFocusToMore.current = true;
      return;
    }
    toggleRef.current?.focus({ preventScroll: true });
  }

  useEffect(() => {
    if (open) return;
    openedFromMore.current = false;
    if (!restoreFocusToMore.current) return;
    restoreFocusToMore.current = false;
    (moreRef.current ?? toggleRef.current)?.focus({ preventScroll: true });
  }, [open]);

  // Only an explicit toggle is remembered. Revealing the group somebody just
  // navigated into is a courtesy for this visit, not them changing their mind
  // about how the navigation should sit tomorrow.
  function toggleGroup(label: string) {
    const collapse = !collapsedGroups.has(label);
    writeChoice(groupPreferenceKey(label), collapse ? "collapsed" : "expanded");
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (collapse) next.add(label);
      else next.delete(label);
      return next;
    });
  }

  function keepFocusInDrawer(event: KeyboardEvent<HTMLElement>) {
    // Only while this is a drawer. Above 900px the same element is a permanent
    // column beside the page, not something laid over it, so there is nothing
    // to trap focus inside and nothing for Escape to close. Trapping there
    // swallowed Tab off the last control in the sidebar — and sent it to the
    // close button, which that layout hides, so focus did not move at all and
    // the keyboard could not reach the page.
    if (!open || !compact) return;

    if (event.key !== "Tab") return;

    const focusable = Array.from(
      drawerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    ).filter(isReachable);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function closeAfterNavigation(event: MouseEvent<HTMLElement>) {
    if (open && event.target instanceof Element && event.target.closest("a")) {
      closeAndRestoreFocus();
    }
  }

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="mobile-nav-bar">
        <div className="mobile-brand" aria-label={`${brand.title} ${brandSubtitle}`}>
          <img className="mobile-brand-icon" src={favicon} alt="" aria-hidden="true" />
          <span className="mobile-brand-label">{brandSubtitle}</span>
        </div>
        <div className="mobile-nav-actions">
          {compact && headerAction}
          <button
            ref={toggleRef}
            type="button"
            className="menu-toggle"
            aria-expanded={open}
            aria-controls={navigationId}
            aria-label={open ? "Close navigation" : "Open navigation"}
            onClick={() => {
              openedFromMore.current = false;
              setOpen((current) => !current);
            }}
          >
            <span aria-hidden="true" className="menu-icon">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </header>

      {compact && mobileQuickItems && !open && (
        <nav className="family-home-mobile-nav" aria-label="Family shortcuts" data-bottom-obstruction="dock">
          {mobileQuickItems.map((item) => (
            <Link key={item.to} to={item.to} aria-current={sidebarItemAriaCurrent(item, pathname)}>{item.label}</Link>
          ))}
          <button
            ref={moreRef}
            type="button"
            className="action-tertiary"
            aria-controls={navigationId}
            aria-expanded={open}
            onClick={() => {
              openedFromMore.current = true;
              setOpen(true);
            }}
          >More</button>
        </nav>
      )}

      {compact && pathname === "/today" && !open && (
        <QuickActionsMenu
          actions={quickActions}
          floating
          onNavigate={(destination) => {
            quickActionDestination.current = destination.split("?")[0]!;
          }}
        />
      )}

      {open && (
        <div
          className="sidebar-backdrop"
          aria-hidden="true"
          onClick={closeAndRestoreFocus}
        />
      )}

      <nav
        ref={drawerRef}
        id={navigationId}
        className="sidebar"
        data-open={open ? "true" : "false"}
        aria-label={`${brandSubtitle} navigation`}
        onKeyDown={keepFocusInDrawer}
        onClickCapture={closeAfterNavigation}
      >
        <div className="sidebar-head">
          <Brand
            title={brand.title}
            subtitle={brandSubtitle}
            className="sidebar-brand"
          />
          {!compact && headerAction}
          <button
            type="button"
            className="drawer-close quiet"
            aria-label="Close navigation"
            onClick={closeAndRestoreFocus}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="sidebar-quick-actions">
          <QuickActionsMenu actions={quickActions} />
        </div>
        <div className="sidebar-sections">
          {sections.map((section, index) => {
            const contentId = `${navigationId}-section-${index}`;
            const expanded = section.label
              ? !collapsedGroups.has(section.label)
              : true;
            const hiddenBadges = expanded
              ? []
              : section.items.filter((item) => badgeShows(item.badge));
            return (
              <section className="nav-section" key={`section-${index}`}>
                {section.label && (
                  <h2 className="nav-heading">
                    <button
                      type="button"
                      className="nav-heading-toggle"
                      aria-expanded={expanded}
                      aria-controls={contentId}
                      onClick={() => toggleGroup(section.label!)}
                    >
                      <span>{section.label}</span>
                      {/* A badge is there to be noticed, so folding the group
                          away must not take it out of sight. It rides up onto
                          the heading, inside the button, so what it is counting
                          is still announced with the group it belongs to. */}
                      {hiddenBadges.length > 0 && (
                        <span className="nav-heading-badges">
                          {hiddenBadges.map((item) => (
                            <span key={item.to}>{item.badge}</span>
                          ))}
                        </span>
                      )}
                      <span
                        className="nav-heading-chevron"
                        aria-hidden="true"
                      />
                    </button>
                  </h2>
                )}
                <div
                  className="nav-section-links"
                  id={section.label ? contentId : undefined}
                  hidden={section.label ? !expanded : false}
                >
                  {section.items.map((item) => {
                    const active = sidebarItemIsActive(item, pathname);
                    const ariaCurrent = sidebarItemAriaCurrent(item, pathname);
                    return (
                      <Link
                        key={item.to}
                        className={active ? "nav active" : "nav"}
                        to={item.to}
                        aria-current={ariaCurrent}
                      >
                        <span className="sidebar-nav-label">
                          <SidebarIcon to={item.to} />
                          <span>{item.label}</span>
                        </span>
                        {item.badge}
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
        <div className="sidebar-foot">{footer}</div>
      </nav>
    </>
  );
}
